import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { auth, db, storage } from "./firebase"; 
import { onAuthStateChanged, signOut } from "firebase/auth";
import { LoginModal } from "./LoginModal";
import "./styles.css";
import VinylPlayer from "./VinylPlayer"; 
import AuthAction from './AuthAction';
import { 
  doc, onSnapshot, query, orderBy, collection, getDoc, where,
  setDoc, addDoc, deleteDoc, updateDoc, arrayUnion, arrayRemove, increment, writeBatch, getDocs
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import Home from './Home';
import ShareModal from './ShareModal';
import ArtistOverlay from './ArtistOverlay';
import RadioPage from "./RadioPage";
import ProfilePage from "./ProfilePage";
import InviteOnboarding from "./InviteOnboarding";
import { getGlobalStats, formatAirtime, AnalyticsDashboard } from './services/AnalyticsEngine';
import { calculateTrackScore } from './services/AnalyticsEngine';

// --- HILFSFUNKTIONEN ---
const shuffleArray = (array) => {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
};

const proxyAudioUrl = (url) => {
  if (!url || !url.includes("firebasestorage.googleapis.com")) return url;
  // ✅ encodeURIComponent encodiert auch & und ? korrekt
  return `https://supermedia-newsletter.procreaction.workers.dev/audio?url=${encodeURIComponent(url)}`;
};

// --- KOMPONENTE: TERMS MODAL ---
function TermsModal({ onAccept, onDecline }) {
  const [hasScrolled, setHasScrolled] = React.useState(false);
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 20) setHasScrolled(true);
  };

  return (
    <div style={{ 
      position: "fixed", top: 0, left: 0, width: "100%", height: "100%", 
      background: "rgba(0,0,0,0.95)", display: "flex", alignItems: "center", 
      justifyContent: "center", zIndex: 9999, padding: "20px" 
    }}>
      <div style={{ 
        background: "#1a1f2b", padding: "25px", borderRadius: "20px", 
        width: "100%", maxWidth: "450px", border: "1px solid #00acc1", color: "white" 
      }}>
        <h2 style={{ color: "#00acc1", marginBottom: "15px", fontSize: "1.2rem", fontWeight: "900" }}>NUTZUNGSBEDINGUNGEN</h2>
        <div onScroll={handleScroll} style={{ height: "250px", overflowY: "auto", background: "#080808", padding: "15px", borderRadius: "10px", fontSize: "0.75rem", lineHeight: "1.5", color: "#ccc", border: "1px solid #2d3446" }}>
          <h4 style={{ color: "#fff" }}>1. Hosting & Zweck</h4>
          <p>[streetwise RADIO] ist eine Hosting-Plattform für Audio-Inhalte zum Zweck des privaten Austauschs innerhalb der Nutzergruppe.</p>
          <h4 style={{ color: "#fff", marginTop: "15px" }}>2. Urheberrechte & Lizenzen</h4>
          <p>Alle Rechte verbleiben beim Urheber. Du räumst uns technisch notwendige Rechte ein.</p>
          <h4 style={{ color: "#fff", marginTop: "15px" }}>3. Nutzerverantwortung</h4>
          <p>Du garantierst, dass du alle Rechte an den Inhalten besitzt.</p>
          <h4 style={{ color: "#fff", marginTop: "15px" }}>4. Take Down</h4>
          <p>Bei Verletzungen werden Inhalte umgehend entfernt.</p>
          <p style={{ marginTop: "20px", color: "#00acc1", fontWeight: "bold", textAlign: "center" }}>
            {hasScrolled ? "✓ Gelesen" : "Bitte bis zum Ende scrollen..."}
          </p>
        </div>
        <button 
          onClick={onAccept} 
          disabled={!hasScrolled} 
          style={{ width: "100%", padding: "12px", background: hasScrolled ? "#00acc1" : "#333", color: "#000", border: "none", borderRadius: "10px", fontWeight: "900", marginTop: "20px", cursor: hasScrolled ? "pointer" : "not-allowed" }}
        >
          AKZEPTIEREN & ALS ARTIST STARTEN
        </button>
        <button 
          onClick={onDecline} 
          style={{ width: "100%", padding: "10px", background: "none", color: "#666", border: "1px solid #222", borderRadius: "10px", fontSize: "0.7rem", fontWeight: "bold", marginTop: "10px", cursor: "pointer" }}
        >
          ERST MAL NUR ZUHÖREN (SELECTOR)
        </button>
      </div>
    </div>
  );
}

// --- HAUPT-APP ---
export default function App() {
  // Check ob Auth-Action Mode
  const params = new URLSearchParams(window.location.search);
  const isAuthAction = params.get('mode') && params.get('oobCode');
   
  // --- UI STATE ---
  const [activeArtist, setActiveArtist] = useState(null);
  const [shareData, setShareData] = useState({ visible: false, url: "", text: "" });
  const [currentPage, setCurrentPage] = useState("radio"); 
  const [toast, setToast] = useState({ message: "", visible: false, type: "success" });
  const [showLogin, setShowLogin] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showInviteOnboarding, setShowInviteOnboarding] = useState(false);
  const [loginMode, setLoginMode] = useState("login"); // "login" | "register"

  // --- USER STATE ---
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isArtist, setIsArtist] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);

  
  // Ref um die gewählte Invite-Rolle zwischen Login und Auth-Callback zu merken
  const inviteIntendedRoleRef = useRef(null);

  // --- TRACK STATE ---
  const [tracks, setTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [playHistory, setPlayHistory] = useState([]);
  const playHistoryRef = useRef([]); 
  const currentTrackIndexRef = useRef(0);
  const globalStats = useMemo(() => getGlobalStats(tracks), [tracks]);
  const currentTrackRef = useRef(null);
  const [showAllTracks, setShowAllTracks] = useState(false);


  // --- PLAYER STATE ---
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isShuffle, setIsShuffle] = useState(true); 
  const audioRef = useRef(null);

  // ─── MIXCLOUD STATE ───────────────────────────────
  const [mixcloudQueue, setMixcloudQueue] = useState([]);
  // { id, title, mixcloudKey, type: 'mixcloud' }
  const [activeMixcloudItem, setActiveMixcloudItem] = useState(null);

  // ✅ Nur initialisieren wenn djMode aktiv:
  const djMode = useMemo(() => 
    allUsers.find(u => u.id === user?.uid)?.djMode || false,
    [allUsers, user]
  );

  const fxInitialized = useRef(false);
  const audioContextRef = useRef(null);
  const limiterNodeRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const gainNodeRef = useRef(null);

  // ✅ NEU: AudioContext beim Zurückkehren aus dem Hintergrund wiederbeleben
  useEffect(() => {
    const resumeAudio = () => {
      if (audioContextRef.current?.state === "suspended") {
        audioContextRef.current.resume().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", resumeAudio);
    window.addEventListener("pageshow", resumeAudio);
    window.addEventListener("focus", resumeAudio);
    return () => {
      document.removeEventListener("visibilitychange", resumeAudio);
      window.removeEventListener("pageshow", resumeAudio);
      window.removeEventListener("focus", resumeAudio);
    };
  }, []);


  // --- FILTER STATE ---
  const [activeUserIds, setActiveUserIds] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchTerm, setSearchTerm] = useState("");

  // ─────────────────────────────────────────────
  // HILFSFUNKTIONEN
  // ─────────────────────────────────────────────

  const showToast = (msg, type = "success") => {
    setToast({ message: msg, visible: true, type });
    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 4000);
  };

  const setAudioTime = (time) => {
    if (audioRef.current) audioRef.current.currentTime = time;
  };
  // ✅ NEU:
  const updatePlayHistory = (newHistory) => {
    playHistoryRef.current = newHistory;
    setPlayHistory(newHistory);
  };


  

  // Invite-Link erzeugt /?invite=true, normaler Share die origin URL
  const handleShare = (type) => {
    const base = window.location.origin;
    const url = type === 'app' 
    ? base                                    // ✅ SHARE STATION: nur App URL
    : type === 'invite'
    ? `${base}/?invite=true`                  // ✅ INVITE ARTIST: mit Parameter
    : `${base}/?track=${currentTrack?.id}`;   // ✅ SHARE TRACK: mit Track ID
  const text = type === 'invite'
    ? "Hey, möchtest Du Teil vom streetwise RADIO Collective Studio werden und eigene Tracks hochladen? Dann klick diesen Link:"
    : type === 'app' 
    ? "Hey kennst du schon unser streetwise RADIO – selfmade Tracks aus unserem Freundeskreis:"
    : `Hey, der Track ${currentTrack?.title} ist jetzt LIVE ON AIR im streetwise Radio!`;
  setShareData({ visible: true, url, text });   
};
  

  // ─────────────────────────────────────────────
  // INVITE ONBOARDING LOGIK
  // ─────────────────────────────────────────────

  // Beim App-Start prüfen ob ?invite=true in der URL steht
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("invite") === "true" && !user) {
      setShowInviteOnboarding(true);
    }
    // ✅ NEU: /station redirect
  if (window.location.pathname.includes("/station")) {
    window.history.replaceState(null, "", "/"); // URL sauber machen
    if (!user) setShowLogin(true); // Login öffnen falls nicht eingeloggt
  }
  }, []); // Nur einmal beim Mount

  // User wählt seine gewünschte Rolle im Onboarding
  const handleInviteChoice = (role) => {
    inviteIntendedRoleRef.current = role; // In Ref speichern (überlebt Re-renders)
    setShowInviteOnboarding(false);
    setShowLogin(true);
    setLoginMode("register");
  };

  // ─────────────────────────────────────────────
  // TERMS LOGIK
  // ─────────────────────────────────────────────

  const acceptTerms = async () => {
    if (!user) return;
    await updateDoc(doc(db, "users", user.uid), {
      termsAccepted: true,
      role: "pending",  // Wartet auf Admin-Freischaltung
      isArtist: false   // Erst nach Admin-Approve true
    });
    setShowTerms(false);
    setCurrentPage("profile"); // Direkt zur Profile Page mit Pending-Message
    showToast("Bewerbung gesendet! Ein Admin prüft dein Profil. ✅");
  };

  const declineTerms = async () => {
    if (user) {
      await updateDoc(doc(db, "users", user.uid), { 
        role: "selector",
        isArtist: false 
      });
    }
    setShowTerms(false);
  };

  // ─────────────────────────────────────────────
  // TRACK FILTER (MEMO)
  // ─────────────────────────────────────────────

  const filteredTracks = useMemo(() => {
    let base = [...tracks];
    if (!user) {
      base = base.filter(t => t.onAir === true);
    } else {
      // 2. Logik für EINGELOGGTE User
      // NEU: Wenn "Public Selection" aktiv ist (showAllTracks === false),
      // filtern wir alles raus, was nicht onAir ist – genau wie bei Gästen.
      if (!showAllTracks && activeFilter !== "🔒 HIDDEN") {
        base = base.filter(t => t.onAir === true);
      }

      // Die restlichen Filter (User, Genre, Suche) werden darauf angewendet

      if (activeUserIds.length > 0) base = base.filter(t => activeUserIds.includes(t.userId));
      
      if (activeFilter === "🗑 DISLIKED") {
        base = base.filter(t => (t.dislikes?.length || 0) > 0 && (t.score || 0) <= 0);
      } else if (activeFilter === "🆕 NEW") {  // ✅ NEU: direkt nach DISLIKED
        const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        base = base.filter(t => (t.createdAt?.seconds * 1000) > oneWeekAgo);

      } else if (activeFilter === "🔒 HIDDEN") {           // ✅ NEU
        base = base.filter(t => t.onAir === false);  
      } else if (activeFilter === "TOP ♡") {
        base = base.filter(t => (t.likes?.length || 0) > 0);
      } else if (activeFilter !== "All") {
        base = base.filter(t => t.style === activeFilter);
      }
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        base = base.filter(t => 
          (t.title || "").toLowerCase().includes(s) || 
          (t.artistName || "").toLowerCase().includes(s)
        );
      }
    }
    // Sortierung
    return base.sort((a, b) => {
      switch (sortOrder) {
        case "abc": return (a.title || "").localeCompare(b.title || "");
        case "likes": return (b.likes?.length || 0) - (a.likes?.length || 0);
        case "newest":
        default: return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      }
    });
  }, [tracks, user, activeUserIds, activeFilter, searchTerm, sortOrder, showAllTracks]);

  const currentTrack = useMemo(() => {
    if (activeTrackId) {
      const track = tracks.find(t => t.id === activeTrackId);
      if (track) return track;
    }
    return tracks[currentTrackIndex] || { title: "LOADING...", artist: "STREETWISE", url: "" };
  }, [activeTrackId, tracks, currentTrackIndex]);

  // ✅ NEU: Ref synchron halten
  useEffect(() => {
    currentTrackRef.current = currentTrack;
  }, [currentTrack]);

  // ─────────────────────────────────────────────
  // PLAYER LOGIK
  // ─────────────────────────────────────────────

  // Ref immer synchron halten
  useEffect(() => {
    currentTrackIndexRef.current = currentTrackIndex;
  }, [currentTrackIndex]);

  // Index neu berechnen wenn Filter/Shuffle sich ändert
  useEffect(() => {
    if (activeTrackId && filteredTracks.length > 0) {
      const newIdx = filteredTracks.findIndex(t => t.id === activeTrackId);
      if (newIdx !== -1) {
        setCurrentTrackIndex(newIdx);
      }
    }
  }, [filteredTracks, activeTrackId]);

  // Watcher: Bei URL-Wechsel sofort abspielen (PWA & Android Fix)
  useEffect(() => {
    if (!currentTrack?.url || !audioRef.current) return;
    
    if (audioRef.current.src && audioRef.current.src.endsWith(encodeURI(currentTrack.url))) return;
    
    try {
      const rawAdjustment = Number(currentTrack.volume_adjustment);
      const safeAdjustment = isNaN(rawAdjustment) ? 1.0 : rawAdjustment;
      audioRef.current.volume = Math.max(0, Math.min(1, safeAdjustment));
    } catch (e) {
      console.warn("Volume-Anpassung fehlgeschlagen, nutze Standard:", e);
    }
    
    const wasPlaying = isPlaying;
    audioRef.current.load();
    if (wasPlaying) {
      audioRef.current.play().catch(e => console.log("Playback start verzögert:", e));
    }
  }, [currentTrack.url]);

  useEffect(() => {
    if (!isPlaying || !currentTrack?.id) return;
  
    const interval = setInterval(() => {
      updateDoc(doc(db, "tracks", currentTrack.id), {
        totalPlaytimeSec: increment(1)
      }).catch(() => {});
    }, 1000); // Jede Sekunde +1
  
    return () => clearInterval(interval); // Stoppt wenn Pause oder Track wechselt
  }, [isPlaying, currentTrack?.id]);
  

  const playTrack = async (trackOrIdentifier, forceSelect = false, addToHistory = true) => {
    // 1. Suche den Track sicher anhand der Master-Liste (tracks) oder gefilterten Liste
    let trackToPlay = null;

    if (typeof trackOrIdentifier === 'string') {
        // Suche über die ID (Sicherste Methode aus RadioPage)
        trackToPlay = tracks.find(t => t.id === trackOrIdentifier);
    } else if (typeof trackOrIdentifier === 'object' && trackOrIdentifier !== null) {
        // Falls das ganze Objekt übergeben wurde
        trackToPlay = trackOrIdentifier;
    } else if (typeof trackOrIdentifier === 'number') {
        // Fallback für alten Index-basierten Aufruf aus filteredTracks
        trackToPlay = filteredTracks[trackOrIdentifier];
    }

    if (!trackToPlay || !trackToPlay.id) return;
  
    if (addToHistory && activeTrackId && activeTrackId !== trackToPlay.id) {
      updatePlayHistory([...playHistoryRef.current, activeTrackId].slice(-50));
    }
  
    const idxInFiltered = filteredTracks.findIndex(t => t.id === trackToPlay.id);
    setActiveTrackId(trackToPlay.id);
    setCurrentTrackIndex(idxInFiltered !== -1 ? idxInFiltered : 0);
  
    // Firestore Stats Update
    if (trackToPlay.id) {
      const today = new Date().toISOString().split('T')[0];
      const hour = new Date().getHours().toString();
      updateDoc(doc(db, "tracks", trackToPlay.id), { 
        plays: increment(1),
        [`dailyStats.${today}.plays`]: increment(1),
        [`playsByHour.${hour}`]: increment(1),
      }).catch(() => {});
    }    
  
    if (forceSelect) {
      setIsPlaying(true);
      if (audioRef.current) {
        
        // ✅ NEU: GLOBALER AUDIO MASTER & LIMITER INITIALISIERUNG
        if (djMode && !fxInitialized.current) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = ctx;

            // MediaElementSource vom <audio> Tag (darf nur einmal erstellt werden)
            const source = ctx.createMediaElementSource(audioRef.current);
            sourceNodeRef.current = source;

            // 1. GainNode erstellen (Der "Vorverstärker")
            const gainNode = ctx.createGain();
            // Wert zwischen 0 und 1 (z.B. 0.8 für etwas Headroom)
            gainNode.gain.value = 0.8; 

            // 2. Limiter konfigurieren (Deine korrigierten Werte)
            const limiter = ctx.createDynamicsCompressor();
            limiter.threshold.value = -3.0;
            limiter.knee.value = 10;
            limiter.ratio.value = 20;
            limiter.attack.value = 0.01;
            limiter.release.value = 0.1;

            // 3. Die Kette verbinden (Routing)
            // Quelle -> GainNode -> Limiter -> Lautsprecher (Destination)
            source.connect(gainNode);
            gainNode.connect(limiter);
            limiter.connect(ctx.destination);

            // Referenzen speichern
            gainNodeRef.current = gainNode;
            limiterNodeRef.current = limiter;

            fxInitialized.current = true;
            // ✅ NEU: Auto-Resume wenn iOS den Context suspended
            ctx.onstatechange = () => {
              if (ctx.state === "suspended") {
                ctx.resume().catch(() => {});
              }
            };
            console.log("✅ Master Limiter aktiv");
          } catch (e) {
            console.error("Audio Init Error:", e);
          }
        }

        // Falls der Context durch Browser-Restriktionen "schläft", aufwecken
        if (audioContextRef.current?.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        audioRef.current.load();
        audioRef.current.play().catch(e => console.log("Play failed", e));
      }
    }
  };

  const playPreviousTrack = useCallback(() => {
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    const history = playHistoryRef.current;
    if (history.length > 0) {
      const lastTrackId = history[history.length - 1];
      const lastTrack = tracks.find(t => t.id === lastTrackId);
      if (lastTrack) {
        updatePlayHistory(history.slice(0, -1));
        // ✅ forceSelect: true damit Audio wirklich startet, addToHistory: false
        playTrack(lastTrack, true, false);
      }
    } else {
      if (audioRef.current) audioRef.current.currentTime = 0;
    }
  }, [tracks]);

  // ✅ NEU: Gewichteter Shuffle – seltene Tracks öfter
  const weightedRandomTrack = (tracks) => {
    // Maximale Plays im Pool finden
    const maxPlays = Math.max(...tracks.map(t => t.plays || 0));
    
    // Gewicht = umgekehrt proportional zu Plays
    // Wenig gespielt = hohes Gewicht, viel gespielt = niedriges Gewicht
    const weights = tracks.map(t => {
      const plays = t.plays || 0;
      return maxPlays - plays + 1; // +1 damit nie 0
    });
    
    // Zufällig aus gewichteter Liste wählen
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    let random = Math.random() * totalWeight;
    
    for (let i = 0; i < tracks.length; i++) {
      random -= weights[i];
      if (random <= 0) return i;
    }
    return 0;
  };
  
  const playNextTrack = useCallback((isManual = false) => {
    if (filteredTracks.length === 0) return;
    const stableIndex = currentTrackIndexRef.current;
  
    // Wenn manuell geskippt wird und der Track weniger als 30 Sek lief
    if (isManual && currentTrack?.id && audioRef.current?.currentTime < 30) {
      const today = new Date().toISOString().split('T')[0];
      const trackRef = doc(db, "tracks", currentTrack.id);

      // ✅ Score einheitlich über die echte Formel neu berechnen (inkl. simuliertem Skip)
      const simulatedTrack = {
        ...currentTrack,
        skipCount: (currentTrack.skipCount || 0) + 1,
      };
      const newScore = calculateTrackScore(simulatedTrack);

      updateDoc(trackRef, {
        score: newScore,
        onAir: newScore > 0,
        skipCount: increment(1),
        [`dailyStats.${today}.skips`]: increment(1),
        ...(newScore <= 0 ? { 
          offAirReason: "skip_penalty_reached",
          onAirLog: arrayUnion({ changedAt: new Date(), changedTo: false, reason: "auto_skip_penalty" })
        } : {})
      }).catch((err) => console.error("Penalty Update failed", err));
    }
  
    let nextIndex;
    if (isShuffle) {
      do {
        nextIndex = weightedRandomTrack(filteredTracks);
      } while (nextIndex === stableIndex && filteredTracks.length > 1);
    } else {
      nextIndex = (stableIndex + 1) % filteredTracks.length;
    }
    
    playTrack(nextIndex, true, true);
  }, [filteredTracks, isShuffle, currentTrack?.id, currentTrack?.score]); 

  // WICHTIG: currentTrack?.score muss in die Dependency-List, damit die Penalty den richtigen Wert liest
  
  const togglePlay = () => { 
    if (audioRef.current) audioRef.current.paused 
      ? audioRef.current.play() 
      : audioRef.current.pause(); 
  };

  const toggleDislike = async (trackId, dislikesArray = []) => {
    const voterId = user ? user.uid : ("anon_" + (localStorage.getItem("anon_id") || "guest"));
    const trackRef = doc(db, "tracks", trackId);
    try {
      if (dislikesArray?.includes(voterId)) {
        await updateDoc(trackRef, { dislikes: arrayRemove(voterId) });
      } else {
        await updateDoc(trackRef, { 
          dislikes: arrayUnion(voterId),
          likes: arrayRemove(voterId) 
        });
        showToast("Feedback gesendet!");
      }
    } catch (err) { console.error(err); }
  };

  const hasLoadedTracksRef = useRef(false);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
  
    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack?.title || "STREETWISE RADIO",
      artist: currentTrack?.artist || currentTrack?.artistName || "",
      album: "Collective Studio",
      artwork: [
        { src: currentTrack?.coverUrl || "/streetwise-radio-logo.png", sizes: "512x512", type: "image/png" }
      ]
    });
    // ✅ NEU: Position bei Track-Wechsel auf 0 zurücksetzen
    try {
      navigator.mediaSession.setPositionState({
        duration: 0,
        playbackRate: 1,
        position: 0
      });
    } catch(e) {}
  
    // Action Handlers registrieren
    navigator.mediaSession.setActionHandler("play", () => {
      audioRef.current?.play();
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      audioRef.current?.pause();
    });
    navigator.mediaSession.setActionHandler("previoustrack", () => {
      playPreviousTrack();
    });
    navigator.mediaSession.setActionHandler("nexttrack", () => {
      playNextTrack();
    });
    navigator.mediaSession.setActionHandler("seekforward", () => {
      playNextTrack();
    });
    navigator.mediaSession.setActionHandler("seekbackward", () => {
      playPreviousTrack();
    });
    
  
    // Cleanup
    return () => {
      ["play", "pause", "previoustrack", "nexttrack", "seekforward", "seekbackward"].forEach(action => {
        navigator.mediaSession.setActionHandler(action, null);
      });
    };

  }, [currentTrack?.id, isPlaying ]);
  
  // ✅ Separater useEffect nur für Position – nicht mit Metadata mischen:
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    if (duration > 1 && progress >= 0 && progress <= duration) {
      try {
        navigator.mediaSession.setPositionState({
          duration: duration,
          playbackRate: 1,
          position: progress
        });
      } catch(e) {}
    }
  }, [progress, duration]);


  // ─────────────────────────────────────────────
  // FIREBASE EFFEKTE
  // ─────────────────────────────────────────────

  useEffect(() => {
    // AUTH LISTENER
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const userRef = doc(db, "users", u.uid);
        
        // Prüfen ob User-Dokument bereits existiert
        const userSnap = await getDoc(userRef);
        const intendedRole = inviteIntendedRoleRef.current;

        if (!userSnap.exists()) {
          // Neuer User: createdAt + lastLogin
          const newRole = intendedRole || "selector";
          await setDoc(userRef, { 
            email: u.email,
            role: newRole,
            isArtist: false,
            isAdmin: false,
            termsAccepted: false,
            createdAt: new Date(),
            lastLogin: new Date() // ✅ NEU
          });
          if (intendedRole === "artist") {
            setShowTerms(true);
          }
        } else {
          // Bestehender User: lastLogin immer updaten
          if (intendedRole) {
            if (intendedRole === "artist") {
              await updateDoc(userRef, { role: "pending", lastLogin: new Date(), email: u.email  });
              setShowTerms(true);
            } else {
              await updateDoc(userRef, { role: "selector", isArtist: false, lastLogin: new Date(), email: u.email  });
            }
          } else {
            // ✅ NEU: Normaler Login ohne Invite – lastLogin updaten
            await updateDoc(userRef, { lastLogin: new Date(), email: u.email });
          }
        }
        

        // Ref zurücksetzen
        inviteIntendedRoleRef.current = null;

        // Realtime Listener auf User-Dokument
        const unsubUser = onSnapshot(userRef, (s) => {
          if (s.exists()) {
            const d = s.data();
            setUserName(d.name?.toUpperCase() || "");
            setIsAdmin(d.isAdmin || false);
            setIsArtist(d.isArtist || false);

            // Terms zeigen wenn pending und noch nicht akzeptiert
            // (aber nicht wenn wir sie gerade schon gezeigt haben)
            if (d.role === "pending" && !d.termsAccepted) {
              setShowTerms(true);
            }
          }
        });

        // ✅ NEU: Notifications Listener
        const unsubNotifications = onSnapshot(
          collection(db, "notifications", u.uid, "messages"),
          (snap) => {
            setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          }
        );

        // Cleanup des User-Listeners wenn Auth-Listener sich erneuert
        return () => { unsubUser(); unsubNotifications(); };
      } else {
        setUserName("");
        setIsAdmin(false);
        setIsArtist(false);
      }
    });

    // TRACKS LISTENER
    const unsubTracks = onSnapshot(
      query(collection(db, "tracks"), orderBy("createdAt", "desc")), 
      snap => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        if (!hasLoadedTracksRef.current) {
          // Erster Load: shufflen und zufällig starten
          hasLoadedTracksRef.current = true;
          const shuffled = shuffleArray(data);
          setTracks(shuffled);
          // ✅ NEU: Alle Artist IDs initial als aktiv setzen:
          const allIds = [...new Set(data.map(t => t.userId))];
          setActiveUserIds(allIds);

          // ✅ NEU: ?track= URL Parameter prüfen
          const params = new URLSearchParams(window.location.search);
          const trackIdFromUrl = params.get("track");

          if (trackIdFromUrl) {
            const targetTrack = data.find(t => t.id === trackIdFromUrl);
            if (targetTrack) {
              const idxInShuffled = shuffled.findIndex(t => t.id === trackIdFromUrl);
              setCurrentTrackIndex(idxInShuffled !== -1 ? idxInShuffled : 0);
              setActiveTrackId(trackIdFromUrl);
            }
          } else {
            // Normal: zufällig starten
            const randomStart = Math.floor(Math.random() * shuffled.length);
            setCurrentTrackIndex(randomStart);
            setActiveTrackId(shuffled[randomStart]?.id || null);
          }

        } else {
          setTracks(data);
        }
        
      }
    );

    // ALL USERS LISTENER
    const unsubAllUsers = onSnapshot(
      collection(db, "users"), 
      snap => setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    return () => { 
      unsubAuth(); 
      unsubTracks(); 
      unsubAllUsers(); 
    };
  }, []);

  // Falls Auth-Action, zeige nur diese Page
  if (isAuthAction) {
    return <AuthAction />;
  }
  

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#000", color: "#fff", fontFamily: "sans-serif" }}>
      
      {/* INVITE ONBOARDING (nur bei ?invite=true) */}
      {showInviteOnboarding && (
        <InviteOnboarding
          onChoose={handleInviteChoice}
          onClose={() => setShowInviteOnboarding(false)}
        />
      )}

      {/* TERMS MODAL */}
      {showTerms && (
        <TermsModal 
          onAccept={acceptTerms} 
          onDecline={declineTerms} 
        />
      )}

      {/* AUDIO ELEMENT */}
      <audio 
        ref={audioRef} 
        src={currentTrack.url} 
        playsInline
        crossOrigin="anonymous" 
        preload="auto"
        onTimeUpdate={() => setProgress(audioRef.current.currentTime)} 
        onLoadedMetadata={() => setDuration(audioRef.current.duration)} 
        onEnded={() => {
          // ✅ NEU: Completion tracken
          const track = currentTrackRef.current; // ✅ immer aktuell
          if (track?.id) {
            const today = new Date().toISOString().split('T')[0];
            const hour = new Date().getHours().toString();
            updateDoc(doc(db, "tracks", track.id), {
              completions: increment(1),
              [`playsByHour.${hour}`]: increment(1),
              [`dailyStats.${today}.completions`]: increment(1),
            }).catch(e => console.error("Completion update failed:", e));
          }
          playNextTrack(false);
        }}

        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* HEADER */}
      <header style={{ borderBottom: "1px solid #111", background: "rgba(0,0,0,0.5)", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ overflow: "hidden", position: "relative" }}>
        {/* Innerer Container: zentriert wenn Platz, sonst links */}
          <div style={{ 
            display: "inline-flex", minWidth: "100%", padding: "1.5rem", paddingRight: "140px",
            justifyContent: "center", alignItems: "center", boxSizing: "border-box"
            }}>          
            {/* LINKS: LOGO & TITEL */}
            <div onClick={() => setCurrentPage("home")} 
              style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: "12px", margin: "0 auto" }} >
              <img src="/streetwise-radio-logo.png" alt="Streetwise Logo" style={{ width: "29px", height: "29px", objectFit: "contain" }}/>
              <div className="logo-text-wrapper" style={{ fontFamily: "'Montserrat', sans-serif", textTransform: "uppercase", letterSpacing: "2px"}}>
                <div style={{ display: "flex", gap: "5px" }}>
                  <span style={{ fontWeight: "600", color: "#FFFFFF", fontSize: "0.8rem" }}>STREETWISE</span>
                  <span style={{ fontWeight: "400", color: "#FFFFFF", fontSize: "0.8rem" }}>RADIO</span>
                </div>
                
                <span className="studio-tag" style={{ fontWeight: "400", color: "#00acc1", marginLeft: "10px", fontSize: "0.8rem" }}>
                  COLLECTIVE STUDIO
                </span>
              </div>
            </div>
          </div>
          <div className="header-buttons" style={{ display: "flex", gap: "12px", alignItems: "center", position: "absolute", right: "1.5rem", top: "50%", transform: "translateY(-50%)", flexDirection: "row", }}>
            {/* Login Button für nicht eingeloggte User */}
            {!user && (
              <button 
              onClick={() => setShowInviteOnboarding(true)}  
                style={{ background: "none", border: "1px solid #333", color: "#666", fontSize: "0.6rem", fontWeight: "bold", padding: "6px 12px", borderRadius: "20px", cursor: "pointer" }}
              >
                JOIN
              </button>
            )}
            <button 
              onClick={() => user ? setCurrentPage("profile") : (() => { setLoginMode("login"); setShowLogin(true); })()}  
              style={{ background: "none", border: "none", color: "#00acc1", fontSize: "0.7rem", fontWeight: "bold", position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}
            >
              {user ? userName || "PROFILE" : "LOGIN"}

              {/* ✅ Roter Punkt unter dem Namen */}
              {user && (
                (isAdmin && allUsers.filter(u => u.role === "pending").length > 0) ||
                notifications.some(n => !n.read)
              ) && (
                <span style={{ 
                  width: "6px", height: "6px", borderRadius: "50%", 
                  background: "#ff1744", display: "block",
                  marginTop: "3px"  // kleiner Abstand unter dem Namen
                }} />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* STATS TICKER – für alle User */}
      <div className="ticker-container">
        <div className="ticker-animation">
          {[0, 1, 2, 3].map(i => (
            <span className="ticker-row" key={i}>
              {[
                { label: "TRACKS", val: globalStats.totalTracks },
                { label: "PLAYS", val: globalStats.totalPlays },
                { label: "AIRTIME", val: formatAirtime(globalStats.totalPlaytimeMin) },
                { label: "AUDIENCE", val: globalStats.totalUsers },
                { label: "LOVE", val: globalStats.totalLikes },
                { label: "ACTIVE ARTISTS", val: globalStats.totalArtists },
                { label: "NEW TRACKS THIS WEEK", val: globalStats.newTracksThisWeek },
              ].map((item, j) => (
                <React.Fragment key={j}>
                  <span className="ticker-label">{item.label}</span>
                  <span className="ticker-value">{item.val}</span>
                </React.Fragment>
              ))}
              <span className="ticker-divider">//</span>
            </span>
          ))}
        </div>
      </div>
     

      {/* ARTIST-FILTER BALKEN */}
      {user && (currentPage === "radio" || currentPage === "profile") && (
        // ✅ Äusserer div: nur scrollbar
        <div style={{ overflowX: "auto", borderBottom: "1px solid #111", scrollbarWidth: "none" }}>
          {/* ✅ Innerer div: zentriert mit inline-flex Trick */}
          <div style={{ display: "inline-flex", minWidth: "100%", gap: "12px", padding: "15px 20px", justifyContent: "center", boxSizing: "border-box" }}>
            {allUsers
              .filter(u => (u.isArtist || u.isAdmin) && u.name)
              .sort((a, b) => {
                const aHasTracks = tracks.some(t => t.userId === a.id);
                const bHasTracks = tracks.some(t => t.userId === b.id);
                if (aHasTracks && !bHasTracks) return -1;
                if (!aHasTracks && bHasTracks) return 1;
                return 0;
              })
              .map(u => (
                <div 
                  key={u.id}
                  onClick={() => {
                    const hasTracks = tracks.some(t => t.userId === u.id);
                    if (!hasTracks) {
                      showToast(`${u.name} hat noch keine Tracks hochgeladen.`, "error");
                      return;
                    }
                    setActiveUserIds(prev => 
                      prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]
                    );
                  }}
                  style={{
                    cursor: "pointer", textAlign: "center", minWidth: "50px",
                    opacity: (() => {
                      const hasTracks = tracks.some(t => t.userId === u.id);
                      if (!hasTracks) return 0.2;
                      return activeUserIds.length === 0 || activeUserIds.includes(u.id) ? 1 : 0.3;
                    })()
                  }}                
                >
                  <div style={{ 
                    width: "42px", height: "42px", borderRadius: "50%", margin: "0 auto",
                    background: u.avatar ? `url(${u.avatar}) center/cover` : `url(/streetwise-radio-logo.png) center/cover`,
                    border: activeUserIds.includes(u.id) ? "2px solid #00acc1" : "1px solid #222"
                  }} />
                  <div style={{ fontSize: "0.5rem", marginTop: "5px", color: activeUserIds.includes(u.id) ? "#00acc1" : "#666", fontWeight: "bold" }}>
                    {u.name?.split(" ")[0].toUpperCase()}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* MAIN CONTENT */}
      <main style={{ paddingBottom: "120px" }}>
        {currentPage === "home" && (
          <Home 
            setCurrentPage={setCurrentPage} 
            profiles={allUsers.filter(u => u.isArtist)} 
            allTracks={tracks} 
            setActiveArtist={setActiveArtist} 
          />
        )}
        
        {currentPage === "radio" && (
          <RadioPage 
            user={user} 
            isUserAdmin={isAdmin} 
            isUserArtist={isArtist}
            audioRef={audioRef}
            tracks={filteredTracks} 
            filteredTracks={filteredTracks} 
            isPlaying={isPlaying} 
            isStationPath={!!user}
            togglePlay={togglePlay} 
            playTrack={playTrack} 
            playNextTrack={playNextTrack}
            playPreviousTrack={playPreviousTrack}
            currentTrackIndex={currentTrackIndex} 
            currentTrack={currentTrack}
            progress={progress} 
            duration={duration}
            setAudioTime={setAudioTime} 
            allUsers={allUsers} 
            activeFilter={activeFilter} 
            setActiveFilter={setActiveFilter}
            sortOrder={sortOrder} 
            setSortOrder={setSortOrder}
            showToast={showToast} 
            handleShare={handleShare} 
            searchTerm={searchTerm}        
            setSearchTerm={setSearchTerm}
            isShuffle={isShuffle} 
            setIsShuffle={setIsShuffle}
            onLoginClick={() => { setLoginMode("login"); setShowLogin(true); }}
            onJoinClick={() => setShowInviteOnboarding(true)}
            djMode={allUsers.find(u => u.id === user?.uid)?.djMode || false}
            audioContextRef={audioContextRef}
            limiterNodeRef={limiterNodeRef}
            sourceNodeRef={sourceNodeRef}
            showAllTracks={showAllTracks}
            setShowAllTracks={setShowAllTracks}
            mixcloudQueue={mixcloudQueue}
            setMixcloudQueue={setMixcloudQueue}
            activeMixcloudItem={activeMixcloudItem}
            setActiveMixcloudItem={setActiveMixcloudItem}
          />
        )}

        {currentPage === "profile" && (
          <ProfilePage 
            user={user} 
            isAdmin={isAdmin} 
            isArtist={isArtist} 
            allUsers={allUsers} 
            pendingUsers={allUsers.filter(u => u.role === "pending")}
            tracks={tracks}
            showToast={showToast} 
            shareInvite={() => handleShare('invite')}
            updateUserRole={async (uid, newRole, djMode) => {
              const isNowAdmin = newRole === "admin";
              
              // ✅ Ein einzelnes updateData Objekt:
              const updateData = { 
                role: isNowAdmin ? "admin" : newRole, 
                isAdmin: isNowAdmin,
                isArtist: newRole === "artist" || isNowAdmin 
              };
              
              // ✅ djMode nur hinzufügen wenn explizit übergeben:
              if (djMode !== undefined) {
                updateData.djMode = djMode;
              }
              
              // ✅ Nur ein einziger updateDoc Aufruf:
              await updateDoc(doc(db, "users", uid), updateData);
              showToast(djMode !== undefined ? `DJ Mode ${djMode ? "aktiviert ✅" : "deaktiviert"} 🎛` : "User updated!");
            }}
            approveUser={async (uid) => {
              await updateDoc(doc(db, "users", uid), { role: "artist", isArtist: true });
              showToast("User approved! ✅");
            }}
            rejectUser={async (uid) => {
              await updateDoc(doc(db, "users", uid), { role: "selector", isArtist: false });
              showToast("User declined.");
            }}
            deleteUser={async (uid) => {
              if (window.confirm("Wirklich löschen?")) {
                await deleteDoc(doc(db, "users", uid));
                showToast("User gelöscht.");
              }
            }}
            openTerms={() => setShowTerms(true)}
            onLoginClick={() => { setLoginMode("login"); setShowLogin(true); }}
            onJoinClick={() => setShowInviteOnboarding(true)}
            profileData={allUsers.find(u => u.id === user?.uid) || {}}
          />
        )}
      </main>

      {/* BOTTOM NAV */}
      <nav className="glass-nav" style={{ 
        position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", 
        width: "90%", maxWidth: "400px", background: "rgba(10,10,10,0.7)", 
        backdropFilter: "blur(20px)", display: "flex", justifyContent: "space-around", 
        padding: "1rem", borderRadius: "20px", border: "1px solid #222", zIndex: 1000 
      }}>
        {["home", "radio", "profile"].map(p => (
          <button 
            key={p} 
            onClick={() => setCurrentPage(p)} 
            style={{ background: "none", border: "none", color: currentPage === p ? "#00acc1" : "#888", fontSize: "0.65rem", fontWeight: "bold" }}
          >
            {p.toUpperCase()}
          </button>
        ))}
      </nav>

      {/* MODALS & OVERLAYS */}
      {showLogin && (
        <LoginModal 
          isOpen={showLogin} 
          onClose={() => setShowLogin(false)} 
          initialMode={loginMode} 
        />
      )}
      {activeArtist && (
        <ArtistOverlay 
          artist={activeArtist} 
          onClose={() => setActiveArtist(null)} 
        />
      )}
      <ShareModal 
        isOpen={shareData.visible} 
        onClose={() => setShareData({ ...shareData, visible: false })} 
        url={shareData.url} 
        text={shareData.text} 
        showToast={showToast} 
      />
      
      {/* TOAST */}
      {toast.visible && (
        <div className="toast-notification" style={{ 
          position: "fixed", bottom: "100px", left: "50%", transform: "translateX(-50%)", 
          background: "#00acc1", color: "#000", padding: "12px 25px", 
          borderRadius: "10px", fontWeight: "bold", zIndex: 10000 
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}